import { Children, cloneElement, createRef, isValidElement, useMemo, useRef } from 'react';
import { SegmentedControlProps, SegmentedControlChild } from './types';

/**
 * Custom hook for managing segmented control items, their refs, and rendering logic.
 * Handles item filtering, focus management, and proper prop injection for accessibility.
 *
 * @param children - React children elements that should be SegmentedControlItem components
 * @param value - Currently selected value in the segmented control
 * @param disabled - Whether the entire segmented control is disabled
 * @param name - Form input name for radio button grouping (ensures mutual exclusivity)
 * @param itemWidth - Width configuration for each item (can be 'content', 'evenly')
 * @param onChange - Callback function triggered when selection changes
 * @param vertical - Whether the segmented control is vertical
 * @param size - Size of the segmented control
 * @returns Object containing item refs, child values, current value, and render function
 */
export const useSegmentedControlItems = (
    children: SegmentedControlProps['children'],
    value: SegmentedControlProps['value'],
    disabled: boolean | undefined,
    name: string | undefined,
    itemWidth: SegmentedControlProps['itemWidth'],
    onChange: SegmentedControlProps['onChange'],
    vertical: SegmentedControlProps['vertical'],
    size: SegmentedControlProps['size']
) => {
    // Map to store refs for each item by their value - enables direct DOM access for keyboard navigation
    const itemRefs = useRef<Map<string, React.RefObject<HTMLButtonElement>>>(new Map());

    // Filter children to only include valid, enabled SegmentedControl items
    // This prevents disabled items from being included in keyboard navigation
    const enabledChildren = useMemo(
        () =>
            Children.toArray(children).filter(
                (child): child is SegmentedControlChild => isValidElement(child) && !disabled && !child.props.disabled
            ),
        [children, disabled]
    );

    const childValues = useMemo(() => enabledChildren.map((child) => child.props.value), [enabledChildren]);
    const firstChildValue = enabledChildren[0]?.props.value;

    // Ensure currentValue is always a string for consistent comparison
    const currentValue = value !== undefined ? String(value) : undefined;

    // Determine which item should be focusable (have tabIndex 0)
    // If there's a selection, focus that; otherwise focus the first item
    const focusableItemValue = currentValue !== undefined ? currentValue : firstChildValue;

    const renderChildren = () => {
        return Children.map(children, (child) => {
            if (!child) return null;

            const childValue = child.props.value;

            // Lazy creation of refs - only create when needed to avoid memory leaks
            // Each item needs a ref for direct DOM manipulation during keyboard navigation
            let ref = itemRefs.current.get(childValue);
            if (!ref) {
                ref = createRef<HTMLButtonElement>();
                itemRefs.current.set(childValue, ref);
            }

            return cloneElement(child, {
                ...child.props,
                disabled: disabled || child.props.disabled,
                checked: childValue === currentValue,
                name,
                itemWidth,
                onChange,
                // Only one item should be focusable at a time
                // This prevents tab navigation from stopping at every item
                tabIndex: childValue === focusableItemValue ? 0 : -1,
                ref,
                vertical,
                size,
            });
        });
    };

    return {
        itemRefs,
        childValues,
        currentValue,
        renderChildren,
    };
};
